import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { timePeriodLabels } from '../constant';
import * as CallIntelTypes from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
/**
 * To get the drill down filter keys for overview page
 */
export const getDrillDownFilterKeys = ({
  id,
  type,
}: CallIntelTypes.CallIntelSubView): CallIntelTypes.DrillDownOptions | undefined => {
  if (!id || !type) return undefined;

  switch (type) {
    case 'appointment-type':
      return {
        index: CallIntelTypes.FilterTypeEnum.FILTER_BY_APPOINTMENT_TYPE,
        key: 'appointment_types',
        value: [id],
      };

    case 'category':
      return {
        index: CallIntelTypes.FilterTypeEnum.FILTER_BY_CATEGORY,
        key: 'categories',
        value: [id],
      };

    case 'sentiment':
      return {
        index: CallIntelTypes.FilterTypeEnum.FILTER_BY_SENTIMENT,
        key: 'sentiments',
        value: [id],
      };

    case 'service-quality':
      return {
        index: CallIntelTypes.FilterTypeEnum.FILTER_BY_SERVICE_QUALITY_FLAG,
        key: 'service_quality_flags',
        value: [id],
      };

    default:
      return undefined;
  }
};

// To get the total number of calls
export const getCallsCount = (
  overview?: CallIntelTypes.Overview,
  sentimentCode?: CallIntelTypes.CallIntelSubViewId
): number => {
  if (sentimentCode) {
    return overview?.sentiments?.[sentimentCode as CallIntelTypes.SentimentEnum] || 0;
  }

  // Each call will have only 1 sentiment, hence total calls = total sentiments
  return Object.values(overview?.sentiments || {}).reduce((acc, count) => acc + count, 0);
};

// Defines if the call is still in processing state
export const isCallInProcessing = (status?: CallIntelTypes.CallStatus): boolean =>
  status === CallIntelTypes.CallStatus.CALL_STATUS_INFERENCING ||
  status === CallIntelTypes.CallStatus.CALL_STATUS_PENDING ||
  status === CallIntelTypes.CallStatus.CALL_STATUS_TRANSCRIBING;

// Identify if the call quality is bad
// mosScore : 1 - 3.5 Poor Quality
// mosScore : > 3.5 to 5 Good Quality
export const isPoorAudio = (call?: CallIntelTypes.Call | null): boolean => {
  if (!call?.mosScore) {
    // Older records might not have mosScore hence do not treat them as of bad quality
    return false;
  }

  return call.mosScore >= 1 && call.mosScore <= 3.5;
};

export const isFailedCall = (status?: CallIntelTypes.CallStatus): boolean => {
  return (
    status === CallIntelTypes.CallStatus.CALL_STATUS_FAILED || status === CallIntelTypes.CallStatus.CALL_STATUS_ERROR
  );
};

export const isSkippedCall = (status?: CallIntelTypes.CallStatus): boolean => {
  return status === CallIntelTypes.CallStatus.CALL_STATUS_SKIPPED;
};

export const getPhoneNumber = (phoneNumber?: CallIntelTypes.PhoneNumber): string => {
  if (!phoneNumber) return '';

  const { countryCode, nationalNumber } = phoneNumber;
  return `${countryCode}${nationalNumber}`;
};

export const isUserInactive = (_officeUser?: CallIntelTypes.OfficeUser | null): boolean => {
  // TODO :: add inactive user logic when we have one, for now return false for all users
  return false;
};

export const isOfficeUserDataUnavailable = (recordDate?: string): boolean => {
  // As confirmed by the BE, office user data is not available prior to 1 April 2024 (including this date)
  // Hence, hard coding the date to 2nd April 2024
  const unavailableOfficeUserDataDate = new Date(2024, 3, 2);
  return !!recordDate && dayjs(recordDate).isBefore(dayjs(unavailableOfficeUserDataDate));
};

export const transformEditableFields = (
  fields: Partial<CallIntelTypes.CallEditableFields>,
  type: CallIntelTypes.TransformEditableFieldType = 'edit'
): Record<string, any> => {
  const transformed: Record<string, any> = {};

  const keyMap: Partial<Record<keyof CallIntelTypes.CallEditableFields, string>> = {
    contactType: 'contact_type',
    schedulingOutcome: 'scheduling_outcome',
    schedulingOpportunity: 'scheduling_opportunity',
    categories: 'categories',
  };

  // direct mappings
  for (const [key, transformedKey] of Object.entries(keyMap) as [keyof CallIntelTypes.CallEditableFields, string][]) {
    if (Object.hasOwn(fields, key)) {
      transformed[transformedKey] = fields[key];
    }
  }

  if (Object.hasOwn(fields, 'callSentiment')) {
    if (fields.callSentiment === null) {
      transformed.call_sentiment = null;
    } else {
      transformed.call_sentiment =
        type === 'edit'
          ? { sentiment: fields.callSentiment }
          : { sentiment: fields.callSentiment?.sentiment ?? fields.callSentiment };
    }
  }

  if (Object.hasOwn(fields, 'appointmentTypes')) {
    if (fields.appointmentTypes === null) {
      transformed.appointment_types = { appointment_type: [] };
    } else {
      transformed.appointment_types =
        type === 'edit'
          ? { appointment_type: fields.appointmentTypes }
          : { appointment_type: fields.appointmentTypes?.appointmentType };
    }
  }

  if (fields.serviceQualityFlags) {
    transformed.service_quality_flags = fields.serviceQualityFlags.map((flag) => ({
      service_quality_flag: flag.serviceQualityFlag,
      is_disabled: flag.isDisabled,
    }));
  }

  return transformed;
};

export const generateListTasksPayload = (callTaskParams: CallIntelTypes.CallTaskParams) => {
  let payload = { filter: {}, include_metadata: false, sort_type: 2 };

  payload.filter = {
    ...payload.filter,
    [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_LOCATION]: {
      location_id: callTaskParams.locations,
    },

    [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_SOURCE]: {
      source: callTaskParams.source || CallIntelTypes.TaskSource.CALL_INTELLIGENCE,
    },

    [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_SOURCE_ID]: {
      source_id: callTaskParams.callIds,
    },

    ...(callTaskParams?.taskStatusFilters?.length && {
      [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_STATUS]: {
        status: callTaskParams.taskStatusFilters,
      },
    }),

    ...(callTaskParams?.taskTypes?.length && {
      [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_TASK_TYPE]: {
        type: callTaskParams.taskTypes,
      },
    }),
  };

  if (callTaskParams.includeMetaData) {
    payload = { ...payload, include_metadata: callTaskParams.includeMetaData };
  }

  if (callTaskParams.sortType) {
    payload = { ...payload, sort_type: callTaskParams.sortType };
  }

  return payload;
};

export const generateCommonFilterPayload = (filters: CallIntelTypes.Filters): CallIntelTypes.FilterPayload => {
  // Get the current timezone offset in minutes
  const timezoneOffsetMinutes = dayjs().utcOffset();
  const start = dayjs(filters.startDate)
    .startOf('day')
    .add(-timezoneOffsetMinutes, 'minute')
    .format('YYYY-MM-DD HH:mm:ss.SSS');

  const end = dayjs(filters.endDate)
    .endOf('day')
    .add(-timezoneOffsetMinutes, 'minute')
    .format('YYYY-MM-DD HH:mm:ss.SSS');

  return {
    filter: {
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_DATE_RANGE]: {
        date_range: {
          end,
          start,
        },
      },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_LOCATION]: {
        locations: filters.locations,
      },
    },
  };
};

export const generateDrillDownPayload = (drillDownOptions: CallIntelTypes.DrillDownOptions) => {
  const { index, key, value } = drillDownOptions;

  return {
    [index]: {
      [key]: value,
    },
  };
};

export const generateChartsPayload = ({ drillDownOptions, filters }: CallIntelTypes.ApiPayloadProps) => {
  const payload = generateCommonFilterPayload(filters);

  if (filters.contactTypes?.length) {
    payload.filter[CallIntelTypes.FilterTypeEnum.FILTER_BY_CONTACT_TYPE] = {
      contact_type: filters.contactTypes,
    };
  }

  payload.filter = {
    ...payload.filter,
    [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
      call_status: [CallIntelTypes.CallStatus.CALL_STATUS_COMPLETE],
    },
  };

  if (drillDownOptions) {
    payload.filter = {
      ...payload.filter,
      ...generateDrillDownPayload(drillDownOptions),
    };
  }

  return payload;
};

export const getFilteredEnumValues = <T extends Record<string, string>>(enumObj: T, excludeValue: T[keyof T]) =>
  Object.values(enumObj).filter((value) => value !== excludeValue);

export const fillTaskTypes = (
  partialTaskTypes: Partial<Record<TaskType, number>>,
  order: Exclude<TaskType, TaskType.TYPE_UNKNOWN>[]
): Record<Exclude<TaskType, TaskType.TYPE_UNKNOWN>, number> => {
  const filledTaskTypes = order.map((key) => [key, partialTaskTypes[key] ?? 0]);

  return Object.fromEntries(filledTaskTypes);
};

export const generateContactId = (call: CallIntelTypes.Call) => {
  if (call.person?.id) {
    return call.person.id;
  } else if (call.phoneNumber?.nationalNumber) {
    return getPhoneNumber(call.phoneNumber);
  }
  return `unknown-contact-${call.id}`;
};

export const getChangeType = (rateChange: number): CallIntelTypes.ChangeType => {
  if (isNaN(rateChange)) return 'noData';
  if (rateChange > 0) return 'increased';
  if (rateChange < 0) return 'decreased';
  return 'unchanged';
};

export const isOngoingLabel = (label: string): boolean => {
  const today = dayjs();

  switch (label) {
    case timePeriodLabels.today:
    case timePeriodLabels['this-week']:
    case timePeriodLabels['this-month']:
    case timePeriodLabels['this-quarter']:
      return true;

    // If it's a custom range, check if it includes today.
    default:
      if (label.includes('-')) {
        const [startLabel, endLabel] = label.split(' - ').map((date) => dayjs(date.trim()));
        return today.isBetween(startLabel, endLabel, 'day', '[]');
      }

      // For all other cases (e.g., "Last Week", "Last Month"), return false
      return false;
  }
};
